import clsx from 'clsx';
import React, { FCWithChildren, useId, useRef, useState } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import styles from './tooltip.module.scss';

type AnimatedTooltipProps = {
  in: boolean;
  direction?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
} & React.HTMLProps<HTMLDivElement>;

const AnimatedTooltip: FCWithChildren<AnimatedTooltipProps> = (props) => {
  const nodeRef = React.useRef(null);
  const { in: inProp, children, direction, delay = 300, ...rest } = props;

  return (
    <Transition
      in={inProp}
      timeout={delay}
      mountOnEnter
      unmountOnExit
      nodeRef={nodeRef}
    >
      {(state: TransitionStatus) => (
        <div
          ref={nodeRef}
          style={{
            opacity: state === 'entered' ? 1 : 0,
          }}
          {...rest}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

export type TooltipProps = {
  tooltipContent: React.JSX.Element | string;
  direction?: 'top' | 'bottom' | 'left' | 'right';
  clickMode?: boolean;
  textTrigger?: boolean;
  delay?: number;
  contentClassName?: string;
} & React.HTMLProps<HTMLDivElement>;

type TooltipContentProps = {
  active?: boolean;
  hideTooltip: () => void;
  closeTooltip?: () => void;
  tooltipId?: string;
} & Omit<TooltipProps, 'textTrigger'>;

const TooltipContent: FCWithChildren<TooltipContentProps> = (props) => {
  const {
    tooltipContent,
    direction,
    clickMode,
    hideTooltip,
    closeTooltip,
    delay,
    active,
    children,
    disabled,
    tooltipId,
    ...rest
  } = props;

  const { className, contentClassName, ...restOfProps } = rest;
  const clickOutsideRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    clickOutsideRef,
    () => {
      if (closeTooltip) {
        closeTooltip();
      } else {
        hideTooltip();
      }
    },
    clickMode,
  );

  return (
    <>
      {children}
      {!disabled && (
        <AnimatedTooltip
          {...restOfProps}
          ref={clickOutsideRef}
          in={active}
          direction={direction}
          delay={delay}
          role="tooltip"
          id={tooltipId}
          className={clsx(styles.TooltipTip, styles[direction], styles[className], contentClassName)}
        >
          {typeof tooltipContent === 'string' ? <span>{tooltipContent}</span> : tooltipContent}
        </AnimatedTooltip>
      )}
    </>
  );
};

const Tooltip: FCWithChildren<TooltipProps> = (props) => {
  const {
    tooltipContent,
    direction = 'top',
    clickMode = false,
    textTrigger = false,
    delay = 300,
    children,
    ...rest
  } = props;
  const { className } = rest;

  const tooltipId = useId();
  const [active, setActive] = useState<boolean>(false);
  const [isClicked, setIsClicked] = useState<boolean>(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const displayTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Only hide if not clicked (for hover behavior)
    if (!isClicked) {
      setActive(false);
    }
  };

  const closeTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsClicked(false);
    setActive(false);
  };

  const handleMouseDown = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (active) {
      setIsClicked(false);
      setActive(false);
    } else {
      setIsClicked(true);
      setActive(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Toggle on Space/Enter but don't set isClicked (allow blur to hide)
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setActive(!active);
    }
  };

  if (clickMode) {
    const { 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy } = rest;
    return (
      <button
        type="button"
        className={clsx(
          styles.TooltipWrapper,
          ['top', 'bottom'].includes(direction) && styles.vertical,
          textTrigger && styles.underlined,
          className,
        )}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        onMouseEnter={displayTooltip}
        onMouseLeave={hideTooltip}
        onFocus={displayTooltip}
        onBlur={hideTooltip}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy || tooltipId}
      >
        <TooltipContent
          hideTooltip={hideTooltip}
          closeTooltip={closeTooltip}
          active={active}
          tooltipId={tooltipId}
          {...props}
        >
          {children}
        </TooltipContent>
      </button>
    );
  }

  return (
    <div
      className={clsx(styles.TooltipWrapper, ['top', 'bottom'].includes(direction) && styles.vertical, className)}
      onMouseEnter={displayTooltip}
      onMouseLeave={hideTooltip}
    >
      <TooltipContent
        hideTooltip={hideTooltip}
        active={active}
        tooltipId={tooltipId}
        {...props}
      >
        {children}
      </TooltipContent>
    </div>
  );
};

export default Tooltip;
